// src/pages/workflows/WorkflowCreate.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
  Card,
  CardContent,
  CardHeader,
  Alert,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Breadcrumbs,
  Link,
  Autocomplete,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  NavigateNext as NextIcon,
} from '@mui/icons-material';

// Import custom components
import { MagicLoader } from '../../design-system';

// Import hooks and stores
import useWorkflowStore from '../../stores/workflowStore';
import useNotificationStore from '../../stores/notificationStore';
import useAuthStore from '../../stores/authStore';

// Mock data for entity selection
// In a real app, you would fetch these from their respective services
const SAMPLE_CONTENT = [
  { _id: 'content1', title: 'Q2 Marketing Campaign Post', type: 'post' },
  { _id: 'content2', title: 'Product Launch Announcement', type: 'announcement' },
  { _id: 'content3', title: 'Holiday Promotion', type: 'promotion' },
];

const SAMPLE_CAMPAIGNS = [
  { _id: 'campaign1', name: 'Summer Collection Launch', type: 'product_launch' },
  { _id: 'campaign2', name: 'Black Friday Promotion', type: 'promotion' },
  { _id: 'campaign3', name: 'Brand Awareness Campaign', type: 'awareness' },
];

const WorkflowCreate = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get template ID from query params if any
  const queryParams = new URLSearchParams(location.search);
  const templateIdParam = queryParams.get('templateId');
  const entityTypeParam = queryParams.get('entityType');
  const entityIdParam = queryParams.get('entityId');
  
  // Store hooks
  const { 
    templates,
    fetchTemplates,
    fetchTemplateById,
    createInstance,
    isLoading, 
    error,
    clearError
  } = useWorkflowStore();
  
  const { showSuccess, showError } = useNotificationStore();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    templateId: templateIdParam || '',
    entityType: entityTypeParam || 'content',
    entityId: entityIdParam || '',
    entityData: null,
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [entities, setEntities] = useState([]);
  
  // Load templates and init form
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load templates if not already loaded
        if (templates.length === 0) {
          await fetchTemplates();
        }
        
        // If template ID is provided, fetch its details
        if (templateIdParam) {
          const template = await fetchTemplateById(templateIdParam);
          setSelectedTemplate(template);
          
          // Set default name based on template
          if (template) {
            setFormData(prev => ({
              ...prev,
              name: `${template.name} - ${new Date().toLocaleDateString()}`,
            }));
          }
        }
        
        // Load entities based on entity type
        loadEntities(formData.entityType);
      } catch (err) {
        showError(err.message || 'Failed to load workflow data');
      }
    };
    
    loadData();
    
    // Cleanup
    return () => {
      clearError();
    };
  }, [fetchTemplates, fetchTemplateById, templateIdParam, clearError]);
  
  // Load entities based on selected type
  const loadEntities = (type) => {
    // In a real app, you would fetch these from APIs
    switch (type) {
      case 'content':
        setEntities(SAMPLE_CONTENT);
        break;
      case 'campaign':
        setEntities(SAMPLE_CAMPAIGNS);
        break;
      default:
        setEntities([]);
    }
    
    // If entity ID is provided, find the entity data
    if (entityIdParam) {
      const found = entities.find(entity => entity._id === entityIdParam);
      if (found) {
        setFormData(prev => ({
          ...prev,
          entityData: found,
          entityId: entityIdParam
        }));
      }
    }
  };
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Reset entity-related fields if entity type changes
    if (name === 'entityType') {
      setFormData({
        ...formData,
        [name]: value,
        entityId: '',
        entityData: null
      });
      
      // Load entities for the new type
      loadEntities(value);
    } else if (name === 'templateId') {
      setFormData({
        ...formData,
        [name]: value
      });
      
      // Find and set the selected template
      const template = templates.find(t => t._id === value);
      setSelectedTemplate(template);
      
      // Update workflow name if it's empty or has default template name
      if (!formData.name || formData.name.startsWith(selectedTemplate?.name)) {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          name: template ? `${template.name} - ${new Date().toLocaleDateString()}` : ''
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    
    // Clear any error for this field
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
  };
  
  // Handle entity selection
  const handleEntitySelect = (event, value) => {
    setFormData({
      ...formData,
      entityId: value?._id || '',
      entityData: value
    });
    
    if (formErrors.entityId) {
      setFormErrors({
        ...formErrors,
        entityId: null
      });
    }
    
    // If entity is selected and name is still default template name, update it
    if (value && selectedTemplate && formData.name === `${selectedTemplate.name} - ${new Date().toLocaleDateString()}`) {
      const entityName = formData.entityType === 'content' ? value.title : value.name;
      setFormData(prev => ({
        ...prev,
        entityId: value._id,
        entityData: value,
        name: `${selectedTemplate.name} - ${entityName}`
      }));
    }
  };
  
  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Workflow name is required';
    }
    
    if (!formData.templateId) {
      errors.templateId = 'Please select a workflow template';
    }
    
    if (!formData.entityType) {
      errors.entityType = 'Please select an entity type';
    }
    
    if (!formData.entityId) {
      errors.entityId = 'Please select an entity';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create workflow instance
      const result = await createInstance({
        name: formData.name,
        description: formData.description,
        templateId: formData.templateId,
        entityType: formData.entityType,
        entityId: formData.entityId,
      });
      
      showSuccess('Workflow started successfully');
      navigate(`/workflows/instances/${result._id}`);
    } catch (err) {
      showError(err.message || 'Failed to start workflow');
      setIsSubmitting(false);
    }
  };
  
  // Get entity label based on type
  const getEntityLabel = () => {
    switch (formData.entityType) {
      case 'content':
        return 'Content Item';
      case 'campaign':
        return 'Campaign';
      case 'form':
        return 'Form Submission';
      default:
        return 'Entity';
    }
  };
  
  // Get entity display field based on type
  const getEntityDisplayField = (entity) => {
    if (!entity) return '';
    
    switch (formData.entityType) {
      case 'content':
        return entity.title;
      case 'campaign':
      case 'form':
        return entity.name;
      default:
        return entity._id;
    }
  };
  
  // Loading state
  if (isLoading && templates.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <MagicLoader variant="magical" size="large" message="Loading workflow templates..." />
      </Box>
    );
  }
  
  return (
    <Box sx={{ py: 3 }}>
      {/* Breadcrumbs navigation */}
      <Breadcrumbs 
        separator={<NextIcon fontSize="small" />} 
        aria-label="breadcrumb"
        sx={{ mb: 2 }}
      >
        <Link 
          component={Link} 
          to="/workflows/instances" 
          underline="hover" 
          color="inherit"
        >
          Workflows
        </Link>
        <Typography color="text.primary">
          Start New Workflow
        </Typography>
      </Breadcrumbs>
      
      <Typography variant="h5" component="h1" gutterBottom>
        Start New Workflow
      </Typography>
      
      <Paper sx={{ p: 3, mt: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Template Selection */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!formErrors.templateId}>
                <InputLabel id="template-select-label">Workflow Template</InputLabel>
                <Select
                  labelId="template-select-label"
                  id="templateId"
                  name="templateId"
                  value={formData.templateId}
                  onChange={handleChange}
                  label="Workflow Template"
                >
                  <MenuItem value="" disabled>
                    <em>Select a workflow template</em>
                  </MenuItem>
                  {templates.map(template => (
                    <MenuItem key={template._id} value={template._id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.templateId && (
                  <FormHelperText>{formErrors.templateId}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            {/* Entity Type Selection */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!formErrors.entityType}>
                <InputLabel id="entity-type-select-label">Entity Type</InputLabel>
                <Select
                  labelId="entity-type-select-label"
                  id="entityType"
                  name="entityType"
                  value={formData.entityType}
                  onChange={handleChange}
                  label="Entity Type"
                >
                  <MenuItem value="content">Content</MenuItem>
                  <MenuItem value="campaign">Campaign</MenuItem>
                  <MenuItem value="form">Form Submission</MenuItem>
                </Select>
                {formErrors.entityType && (
                  <FormHelperText>{formErrors.entityType}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            {/* Entity Selection */}
            <Grid item xs={12}>
              <Autocomplete
                id="entityId"
                options={entities}
                getOptionLabel={(option) => getEntityDisplayField(option)}
                value={formData.entityData}
                onChange={handleEntitySelect}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={getEntityLabel()}
                    error={!!formErrors.entityId}
                    helperText={formErrors.entityId}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            
            {/* Workflow Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="name"
                name="name"
                label="Workflow Name"
                value={formData.name}
                onChange={handleChange}
                error={!!formErrors.name}
                helperText={formErrors.name}
                required
              />
            </Grid>
            
            {/* Workflow Description */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="description"
                name="description"
                label="Description (Optional)"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={1}
              />
            </Grid>
          </Grid>
          
          {/* Template Preview */}
          {selectedTemplate && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Template Preview
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardHeader 
                      title="Template Details"
                      subheader={selectedTemplate.description}
                    />
                    <Divider />
                    <CardContent>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Type:</strong> {selectedTemplate.type}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Steps:</strong> {selectedTemplate.steps?.length || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardHeader title="Workflow Steps" />
                    <Divider />
                    <CardContent>
                      {selectedTemplate.steps && selectedTemplate.steps.length > 0 ? (
                        <Stepper orientation="vertical" sx={{ mt: 2 }}>
                          {selectedTemplate.steps.map((step, index) => (
                            <Step key={index} active={index === 0}>
                              <StepLabel>{step.name}</StepLabel>
                            </Step>
                          ))}
                        </Stepper>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          No steps defined for this template
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
          
          {/* Form Actions */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/workflows/instances')}
            >
              Cancel
            </Button>
            
            <Button
              type="submit"
              variant="contained"
              
              startIcon={<SaveIcon />}
              loading={isSubmitting}
              disabled={isSubmitting}
              magical={true}
            >
              Start Workflow
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default WorkflowCreate;